import $ from 'jquery';
import { isEqual } from 'underscore';
import { namespace } from '../../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractResizeHandle from '../handle/abstractResizeHandle';

/**
 * @extends AbstractResizeHandle
 */
export default class CreateConnectorToolView extends AbstractResizeHandle {
    /**
     * @constructor
     * @param {Ofcs.Editor.Svg.Canvas} canvas
     * @param {Ofcs.Editor.Svg.Tool.CreateConnectorTool} createTool
     */
    constructor(canvas, createTool) {
        super(createTool, canvas);
        this.canvas = canvas;
        this.createTool = createTool;
        this.cache = new Ofcs.Cache();
        this.preview = {};

        this.initializeHandleSymbols(canvas);
        this.updateElementsPositions();

        this.createTool.on('projectConnector', (...args) => this._onUpdate(...args));
        this.createTool.on('abort', (...args) => this._onAbort(...args));

        canvas.on('zoomchange', () => this.updateElementsPositions());
        canvas.on('panchange', () => this.updateElementsPositions());
    }

    /**
     * Handles updates for mousemove events
     * @param {Ofcs.Line} line
     * @param {string} color hex-value for line's color
     * @private
     */
    _onUpdate(line, color) {
        const canvasOptions = this.canvas.options;
        const zoomMatrix = new Ofcs.Matrix();

        this.preview.item = this.cache.get('item', () => {
            const svg = this.canvas.getSvgWrapper();
            const parentEl = $(svg.group(this.canvas.getLayerManager().getElement('ui.handles')));
            return this.createPreview(line, parentEl);
        });

        this.preview.item.attr({ stroke: color });

        if (this.preview.line && !isEqual(line, this.preview.line)) {
            this.preview.line = line;
            const start = this.preview.line.startPoint.clone();
            const end = this.preview.line.endPoint.clone();
            zoomMatrix.scale(canvasOptions.zoom).translate(canvasOptions.panX, canvasOptions.panY);
            start.transform(zoomMatrix);
            end.transform(zoomMatrix);
            this.preview.item.attr({
                x1: start.x,
                y1: start.y,
                x2: end.x,
                y2: end.y,
            });
        } else {
            this.preview.line = line;
        }
    }

    /**
     * Handles abort behavior upon creation abortion
     * @private
     */
    _onAbort() {
        this.remove();
        this.canvas.setActiveTool('select');
    }

    /**
     * Create a connector Preview along the specified line.
     *
     * @protected
     *
     * @param {Ofcs.Line} line
     * @param {element} parentEl
     * @return {jQuery}
     */
    createPreview(line, parentEl) {
        const canvasOptions = this.canvas.options;
        const zoomMatrix = new Ofcs.Matrix();
        const svg = this.canvas.getSvgWrapper();
        const options = {
            strokeWidth: 6,
            class: 'connector-projection',
        };

        zoomMatrix.scale(canvasOptions.zoom).translate(canvasOptions.panX, canvasOptions.panY);

        const point1 = line.startPoint.clone();
        const point2 = line.endPoint.clone();
        point1.transform(zoomMatrix);
        point2.transform(zoomMatrix);

        options['stroke-opacity'] = '0.7';
        return $(svg.line(parentEl, point1.x, point1.y, point2.x, point2.y, options));
    }

    /**
     * Updates the position of internal DOM elements based on the attributes
     * of the first point.
     */
    updateElementsPositions() {
        const { options } = this.canvas;
        const zoomMatrix = new Ofcs.Matrix();

        zoomMatrix.scale(options.zoom).translate(options.panX, options.panY);

        if (this.preview && this.preview.item) {
            const start = this.preview.line.startPoint.clone();
            const end = this.preview.line.endPoint.clone();
            this.preview.item.attr({
                x1: start.x,
                y1: start.y,
                x2: end.x,
                y2: end.y,
            });
        }
    }

    /**
     * Removes the DOM nodes from the canvas and deactivates all event listeners
     */
    remove() {
        const handlesLayer = $(this.canvas.getLayerManager().getElement('ui.handles'));
        handlesLayer.find('g').remove();
        this.preview = {};
        this.cache.clear();
    }
}

namespace('Ofcs.Editor.Svg.ToolView');
window.Ofcs.Editor.Svg.ToolView.CreateConnectorToolView = CreateConnectorToolView;
